import { PLAN_KEYS, WORKFLOW_PROBLEM_DEBOUNCE_MS, type PlanKey, type WorkflowTrigger } from "@sagot/constants";
import { useEffect, useMemo, useState } from "react";
import { incompleteOf } from "./builder/step-summary";
import { checkGraph } from "./graph-check";
import { INCOMPLETE_REASON_OF_PROBLEM, indexProblems, type Problem, type ProblemIndex } from "./problem-model";
import { serverProblemsOf, stillApplies } from "./server-problems";
import type { Definition } from "./types";

/** A refused save: the lines the server wrote, and the version it refused. */
export interface Refusal {
  lines: readonly string[];
  definition: Definition;
}

/** Reasons a step is "incomplete" that the graph check already reports as a problem of its own (so they are not counted twice). */
const REPORTED_BY_GRAPH: ReadonlySet<string> = new Set(Object.values(INCOMPLETE_REASON_OF_PROBLEM));

const planOf = (plan: string | undefined): PlanKey | undefined => PLAN_KEYS.find((key) => key === plan);

/** Waits `ms` after the last change of `value` before it is passed on (the first value passes at once). */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => { setSettled(value); }, ms);
    return () => { window.clearTimeout(timer); };
  }, [value, ms]);
  return settled;
}

/** Steps that still need something (`incompleteOf`), as problems. */
function incompleteProblems(definition: Definition): Problem[] {
  const found: Problem[] = [];
  for (const [path, body] of Object.entries(definition.paths)) {
    for (const step of body.steps) {
      const reason = incompleteOf(step);
      if (reason && !REPORTED_BY_GRAPH.has(reason)) found.push({ code: "incomplete", where: `step ${step.id}`, path, stepId: step.id, params: { reason }, source: "client" });
    }
  }
  return found;
}

interface Options {
  definition: Definition;
  trigger: WorkflowTrigger;
  /** The workspace's plan, once known: the plan rules are left out until then. */
  plan: string | undefined;
  /** The last refused save, while its problems may still apply. */
  refusal: Refusal | null;
}

/**
 * Everything wrong with the workflow right now (WF-P5-16): what the client finds as the author edits (a pause of
 * `WORKFLOW_PROBLEM_DEBOUNCE_MS` after the last edit), the steps that are still incomplete, and what the server said
 * when it refused a save, for as long as the step it names has not been edited since.
 */
export function useWorkflowProblems({ definition, trigger, plan, refusal }: Options): ProblemIndex {
  const settled = useDebounced(definition, WORKFLOW_PROBLEM_DEBOUNCE_MS);
  const known = planOf(plan);
  const found = useMemo(() => [...checkGraph(settled, trigger, known), ...incompleteProblems(settled)], [settled, trigger, known]);
  return useMemo(() => {
    const flagged = new Set(found.map((problem) => problem.where));
    const reported = refusal
      ? serverProblemsOf(refusal.lines, refusal.definition).filter((problem) => !flagged.has(problem.where) && stillApplies(problem, refusal.definition, definition))
      : [];
    return indexProblems([...found, ...reported]);
  }, [found, refusal, definition]);
}
